import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Login from "./pages/Login";
import HomeRedesign from "./components/home/HomeRedesign";
import Unauthorized from "./pages/Unauthorized";
import AuthCallback from "./pages/AuthCallback";
import DispatchBoard from "./components/dispatch/DispatchBoard";
import DroneOps from "./components/drone/DroneOps";
import QCBoard from "./components/qc/QCBoard";
import FinanceDashboard from "./components/finance/FinanceDashboard";
import MyPipeline from "./components/pipeline/MyPipeline";
import AsmBoard from "./pages/AsmBoard";
import DealDetail from "./components/deal/DealDetail";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollRestore from "./components/ScrollRestore";
import JoyOutreachBoard from "./components/outreach/JoyOutreachBoard";
import Notifications from "./pages/Notifications";
import Crew from "./pages/Crew";
import Canvas from "./pages/Canvas";
import CanvasAppt from "./pages/CanvasAppt";
import CanvasApprovals from "./pages/CanvasApprovals";
import CanvasManagerAudit from "./pages/CanvasManagerAudit";
import CanvasActivity from "./pages/CanvasActivity";
import SoftphonePage from "./pages/Softphone";
import RunThePlay from "./pages/RunThePlay";
import MyBusiness from "./pages/MyBusiness";
import BuildCalendar from "./pages/BuildCalendar";
import MyPay from "./pages/MyPay";
import RoofMaster from "./pages/RoofMaster";
import Financials from "./pages/Financials";
import CallList from "./pages/CallList";
import Referrals from "./pages/Referrals";
import TeamBoards from "./pages/TeamBoards";
import HoverIntegration from "./pages/HoverIntegration";
import TeamRoster from "./pages/TeamRoster";
import PmBoard from "./pages/PmBoard";
import Requests from "./pages/Requests";
import AdminHub from "./pages/Admin";
import PhoneHealth from "./pages/PhoneHealth";

/* Homeowner pages (token-based auth, no Google OAuth) */
import StatusPortal from "./components/homeowner/StatusPortal";
import PaymentPortal from "./components/homeowner/PaymentPortal";
import UploadPortal from "./components/homeowner/UploadPortal";
import FeedbackPage from "./components/homeowner/FeedbackPage";
import ColorSelection from "./components/homeowner/ColorSelection";
import TradeAddendum from "./components/homeowner/TradeAddendum";
import ReferralPage from "./components/homeowner/ReferralPage";
import SigningPage from "./components/homeowner/SigningPage";
import HomeownerLanding from "./components/homeowner/HomeownerLanding";
import InvoiceView from "./pages/InvoiceView";
import ICSigningPage from "./pages/ICSigningPage";

// Job analysis (Oct 1, 2026) — loaded on demand: its charts (recharts) are only fetched by the few people who
// open /jobs, so the bundle every field rep downloads stays as it was. A stale chunk after a deploy is caught
// by installChunkReloadGuard (main.tsx).
const JobAnalysis = lazy(() => import("./pages/JobAnalysis"));

/** /jobs and /jobs/:dealId — same audience as /financials. */
function JobAnalysisRoute() {
  return (
    <ProtectedRoute allowedRoles={["admin", "finance", "sales_director"]}>
      <Suspense fallback={<div className="max-w-[1240px] mx-auto p-6 text-slate-500">Loading job analysis…</div>}>
        <JobAnalysis />
      </Suspense>
    </ProtectedRoute>
  );
}

/** true when loaded on the homeowner portal domain */
const isPortalDomain =
  window.location.hostname === "portal.bardinroofing.com";

/** "/pay" serves two people. A homeowner's payment link always carries
 *  ?token= (create-payment-link builds portal.bardinroofing.com/pay?…&token=…),
 *  and on the portal domain the page is always the homeowner's: PaymentPortal,
 *  exactly as before. On the staff domain with no token it is My Pay — the pay
 *  statement emails link to app.bardinroofing.com/pay — so forward it to
 *  /my-pay, which lives inside the signed-in layout. The two can't share one
 *  path: React Router matches the public "/pay" first, so a staff "/pay" route
 *  would never render. (Oct 19, 2026) */
function PayEntry() {
  const location = useLocation();
  if (isPortalDomain || new URLSearchParams(location.search).has("token")) {
    return <PaymentPortal />;
  }
  return <Navigate to={{ pathname: "/my-pay", search: location.search }} replace />;
}

export default function App() {
  return (
    <>
    <ScrollRestore />
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Homeowner landing — only on portal domain */}
      {isPortalDomain && (
        <Route path="/" element={<HomeownerLanding />} />
      )}

      {/* Homeowner pages — public, token-based auth */}
      <Route path="/status" element={<StatusPortal />} />
      {/* /pay = the homeowner payment page; staff without a token go on to /my-pay (PayEntry) */}
      <Route path="/pay" element={<PayEntry />} />
      <Route path="/upload" element={<UploadPortal />} />
      <Route path="/feedback" element={<FeedbackPage />} />
      <Route path="/colors" element={<ColorSelection />} />
      {/* A trade added after the roof agreement was signed: the homeowner signs its add-on (Oct 29, 2026) */}
      <Route path="/trade-addendum" element={<TradeAddendum />} />
      <Route path="/referral" element={<ReferralPage />} />
      <Route path="/sign" element={<SigningPage />} />
      {/* IC Agreement signing — public (token) or authenticated (first-login gate) */}
      <Route path="/ic-sign" element={<ICSigningPage />} />
      {/* Supplement invoice public pay page — anon access (token-resolved) */}
      <Route path="/i/:token" element={<InvoiceView />} />

      {/* Authenticated staff portal */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Promoted Jun 17, 2026: the redesigned home is now the live "/" home.
            It folds in the role-landing redirects (RS → Dispatch) and the
            Log a Lead / Onboard IC actions. */}
        <Route path={isPortalDomain ? "/staff" : "/"} element={<HomeRedesign />} />
        {/* /home-classic (the original card-grid home) retired Aug 24, 2026 —
            the redesign has been the live home since Jun 17 and the classic
            page's cards were absorbed (CommitmentCard → My Business band's
            pulse row; the rest deleted). Redirect stale links home. */}
        <Route path="/home-classic" element={<Navigate to="/" replace />} />
        <Route
          path="/dispatch"
          element={
            <ProtectedRoute
              allowedRoles={[
                "admin",
                "roof_team_manager",
                "roof_specialist",
                "asm",
                "sales_director",
                "qc", "roof_director", "director_of_operations",
              ]}
            >
              <DispatchBoard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/roof-master"
          element={
            <ProtectedRoute
              allowedRoles={[
                "admin",
                "roof_team_manager",
                "roof_specialist",
                "asm",
                "sales_director",
                "qc", "roof_director", "director_of_operations",
              ]}
            >
              <RoofMaster />
            </ProtectedRoute>
          }
        />
        <Route
          path="/call-list"
          element={
            <ProtectedRoute
              allowedRoles={[
                "admin",
                "sales_director",
                "asm",
                "team_lead",
                "restore_manager",
                "director_of_operations",
                "roof_director",
                "office",
              ]}
            >
              <CallList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/referrals"
          element={
            <ProtectedRoute
              allowedRoles={[
                "admin",
                "restore_manager",
                "team_lead",
                "asm",
                "sales_director",
                "finance",
                "roof_director",
                "director_of_operations",
                "office",
              ]}
            >
              <Referrals />
            </ProtectedRoute>
          }
        />
        {/* Ops Requests (Sep 25, 2026): reps file, the Roof Team Manager works
            the list. Same set as homeNav's 🛠 DEST — RLS scopes what each
            role actually sees inside. */}
        <Route
          path="/requests"
          element={
            <ProtectedRoute
              allowedRoles={[
                "admin",
                "restore_manager",
                "team_lead",
                "asm",
                "sales_director",
                "roof_director",
                "director_of_operations",
                "roof_team_manager",
              ]}
            >
              <Requests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/drone"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "drone_pilot", "sales_director", "asm", "report_specialist", "qc", "roof_director", "director_of_operations", "office", "team_lead"]}
            >
              <DroneOps />
            </ProtectedRoute>
          }
        />
        <Route
          path="/qc"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "qc", "roof_director", "director_of_operations", "sales_director", "report_specialist"]}
            >
              <QCBoard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/finance"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "finance", "sales_director"]}
            >
              <FinanceDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations/hover"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "sales_director", "director_of_operations"]}
            >
              <HoverIntegration />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team-boards"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "sales_director", "director_of_operations"]}
            >
              <TeamBoards />
            </ProtectedRoute>
          }
        />
        <Route
          path="/phone-health"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "sales_director", "director_of_operations"]}
            >
              <PhoneHealth />
            </ProtectedRoute>
          }
        />
        <Route
          path="/team-roster"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "sales_director", "director_of_operations", "finance"]}
            >
              <TeamRoster />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "sales_director", "director_of_operations", "finance"]}
            >
              <AdminHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pm-board"
          element={
            <ProtectedRoute
              allowedRoles={[
                "admin", "project_manager", "roof_team_manager", "sales_director",
                "director_of_operations", "finance", "qc", "roof_director", "office",
              ]}
            >
              <PmBoard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/financials"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "finance", "sales_director"]}
            >
              <Financials />
            </ProtectedRoute>
          }
        />
        <Route path="/jobs" element={<JobAnalysisRoute />} />
        <Route path="/jobs/:dealId" element={<JobAnalysisRoute />} />
        <Route
          path="/my-pipeline"
          element={
            <ProtectedRoute
              allowedRoles={[
                "admin",
                "restore_manager",
                "team_lead",
                "asm",
                "sales_director",
                "qc", "roof_director", "director_of_operations",
              ]}
            >
              <MyPipeline />
            </ProtectedRoute>
          }
        />
        {/* Oct 27, 2026 — Alex's expected-build calendar (expected = preferred build date).
            Oct 29, 2026 — open to every team member (Greg: "a resource for all to see"). */}
        <Route
          path="/build-calendar"
          element={
            <ProtectedRoute>
              <BuildCalendar />
            </ProtectedRoute>
          }
        />
        <Route
          path="/run-the-play"
          element={
            <ProtectedRoute
              allowedRoles={[
                "admin",
                "restore_manager",
                "team_lead",
                "asm",
                "sales_director",
                "qc",
                "roof_director",
                "director_of_operations",
              ]}
            >
              <RunThePlay />
            </ProtectedRoute>
          }
        />
        {/* Crew — moved inside the staff shell Aug 24, 2026 (Greg: "bring all
            that together"). Any authenticated team_roster member can view
            (managers get extra capabilities via DB-level crew_is_manager()
            checks). The .crew-app skin survives until the Ship-7 restyle. */}
        <Route path="/crew" element={<Crew />} />
        {/* My Business — the rep's full business snapshot (Ship 4). */}
        <Route
          path="/my-business"
          element={
            <ProtectedRoute
              allowedRoles={[
                "admin",
                "restore_manager",
                "team_lead",
                "asm",
                "sales_director",
              ]}
            >
              <MyBusiness />
            </ProtectedRoute>
          }
        />
        {/* My Pay — each person's paid history, every statement, and their own
            goal slider (Oct 19, 2026). Everyone who is paid commissions,
            overrides, pool shares or SPIFFs; finance + super users can pick anyone.
            Its own path: "/pay" is the public homeowner page (see PayEntry).
            Canvassers too (Oct 26, 2026): hours, appointments and weekly pay. */}
        <Route
          path="/my-pay"
          element={
            <ProtectedRoute
              allowedRoles={[
                "restore_manager",
                "team_lead",
                "asm",
                "roof_specialist",
                "roof_team_manager",
                "sales_director",
                "admin",
                "finance",
                "director_of_operations",
                "canvasser",
              ]}
            >
              <MyPay />
            </ProtectedRoute>
          }
        />
        <Route path="/inbox" element={<Navigate to="/notifications" replace />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/canvas" element={<Canvas />} />
        <Route path="/canvas/appt" element={<CanvasAppt />} />
        <Route path="/canvas/approvals" element={<CanvasApprovals />} />
        <Route path="/canvas/manager-audit" element={<CanvasManagerAudit />} />
        <Route path="/admin/canvas-activity/:userId" element={<CanvasActivity />} />
        <Route path="/softphone" element={<SoftphonePage />} />
        <Route
          path="/outreach"
          element={
            <ProtectedRoute
              allowedRoles={["admin", "office", "qc", "roof_director", "director_of_operations", "sales_director"]}
            >
              <JoyOutreachBoard />
            </ProtectedRoute>
          }
        />
        {/* ASM Approvals board — agreements (first gate) + canvas + drone intake.
            asm = Alex; admin/sales_director keep it as backup; super-users bypass. */}
        <Route
          path="/asm"
          element={
            <ProtectedRoute allowedRoles={["admin", "asm", "sales_director"]}>
              <AsmBoard />
            </ProtectedRoute>
          }
        />
        {/* Legacy routes redirect to unified pipeline */}
        <Route path="/team" element={<Navigate to="/my-pipeline" replace />} />
        <Route path="/rm-commissions" element={<Navigate to="/my-pipeline" replace />} />
        {/* Legacy routes — consolidated into My Pipeline or Finance */}
        <Route path="/rm-dispatch" element={<Navigate to="/my-pipeline" replace />} />
        <Route path="/rm-inbox" element={<Navigate to="/my-pipeline" replace />} />
        <Route path="/commissions" element={<Navigate to="/finance" replace />} />
        <Route path="/deal/:id" element={<DealDetail />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
